// Dublette vor Anlage -- Modell und Fachsprache (ST-DQ-001, CM-CAP-005).
//
// REINES MODELL, kein React, kein DOM -- dieselbe Trennung wie in parties.ts
// und quick-capture.ts und aus demselben Grund: was hier steht, ist ohne
// Browser pruefbar.
//
// DREI ZUSAGEN, die hier und nicht im Bildschirm eingeloest werden:
//
//   1. DAS WORT "DUBLETTE" ERSCHEINT AUF DEM BILDSCHIRM NICHT ALS URTEIL.
//      Ein exakter Treffer auf einer E-Mail-Adresse ist ein KANDIDAT und nie
//      ein Beweis -- `CM-PTY-005` sagt das woertlich, und Migration `0015`
//      erzwingt die Eindeutigkeit deshalb ausdruecklich NICHT. Die Texte
//      formulieren entsprechend: "koennte schon vorhanden sein", nicht "ist
//      bereits vorhanden". Der Unterschied ist nicht Hoeflichkeit: eine
//      Sammeladresse `info@verlag.example` und eine Telefonzentrale sind die
//      Regelfaelle, und wer sie als Beweis liest, fuehrt zwei Menschen
//      zusammen.
//
//   2. DIE OBERFLAECHE FILTERT NICHTS UND ENTSCHEIDET NICHTS. Sie zeigt, was
//      die API liefert. Die Sichtbarkeitsregel steht serverseitig; eine zweite
//      Fassung hier waere die zweite Wahrheit, die CM-SEC-002 ausschliesst.
//      Und es gibt keinen Weg, ueber den die Oberflaeche zusammenfuehrt --
//      Merge und Unmerge gehoeren `CM-CAP-008` und sind nicht gebaut.
//
//   3. KEIN TECHNIKBEGRIFF (NFR-UX-003, CM-UX-010). Die API antwortet mit
//      `kind: 'organization'` und `lifecycleStatus: 'merged'`; auf dem
//      Bildschirm steht "Organisation" und "Zusammengeführt". Die Uebersetzung
//      ist dieselbe Tabelle wie im Leseweg und keine zweite.

import { correlationOf, plainMessageFor, type PlainMessage, type ResponseFacts } from './access.ts';
import type { SendLike } from './parties.ts';

/**
 * Ein Kandidat, so wie ihn die API liefert.
 *
 * SECHS FELDER, UND MEHR GIBT ES NICHT -- die Auswahl ist serverseitig
 * begruendet (apps/api/src/domain/party/duplicate-candidates.ts) und hier
 * bewusst nicht erweitert. Insbesondere kommt der getroffene WERT nicht mit:
 * die Nutzerin hat ihn gerade selbst getippt.
 */
export interface DuplicateCandidate {
  readonly partyId: string;
  readonly kind: string;
  readonly lifecycleStatus: string;
  readonly displayName: string;
  readonly matchesEmailAddress: boolean;
  readonly matchesPhoneNumber: boolean;
}

export type DuplicateCheckResult =
  | {
      readonly outcome: 'geprueft';
      readonly candidates: readonly DuplicateCandidate[];
      readonly hasMore: boolean;
    }
  /**
   * Die Pruefung selbst ist gescheitert.
   *
   * EIGENER AUSGANG UND NICHT "keine Kandidaten". Das ist die wichtigste
   * Unterscheidung dieser Datei: eine gescheiterte Pruefung als "nichts
   * gefunden" zu behandeln waere eine Beruhigung ohne Grundlage -- die
   * Nutzerin legte an, weil ihr niemand widersprochen hat, und niemand hat
   * ueberhaupt nachgesehen.
   */
  | { readonly outcome: 'ungeprueft'; readonly message: PlainMessage };

export const DUPLICATE_HEADING = 'Diesen Kontakt könnte es schon geben';

/**
 * Der Satz, der die Zusage ausspricht.
 *
 * Er steht in der Warnung selbst und nicht im Kleingedruckten, weil er die
 * fachliche Aussage des ganzen Schnitts ist: die Anlage ist NICHT blockiert
 * (`CM-CAP-005`: "ergänzen oder begründet neu anlegen"), und der Treffer ist
 * kein Beweis (`CM-PTY-005`).
 */
export const DUPLICATE_IS_NOT_A_BLOCKER =
  'Das hält dich nicht auf: du kannst den vorhandenen Kontakt öffnen oder den neuen trotzdem anlegen.';

/**
 * Warum ein Treffer noch keine Dublette ist -- in einem Satz.
 *
 * Er nennt den Regelfall, in dem die Warnung falsch liegt, statt ihn der
 * Nutzerin zu ueberlassen. Ohne ihn liest sich "gleiche E-Mail-Adresse" wie
 * ein Beweis, und ein Mensch, der eine Sammeladresse erfasst, gibt entweder
 * auf oder fuehrt zwei Menschen zusammen.
 */
export const DUPLICATE_IS_NOT_A_PROOF =
  'Dieselbe Adresse oder Nummer kann zu mehreren Personen gehören — etwa bei einer Sammeladresse oder einer Zentrale.';

/** Die Beschriftung des Verweises auf einen vorhandenen Kontakt. */
export const OPEN_CANDIDATE_HINT = 'öffnet einen neuen Tab';

/**
 * Warum der vorhandene Kontakt in einem NEUEN Tab aufgeht.
 *
 * Nicht Bequemlichkeit, sondern Datenverlust: dieser Bildschirm hat KEINEN
 * Entwurfsschutz. `CM-UX-004` -- Entwurf automatisch sichern, Undo,
 * Konfliktschutz -- gehoert `ST-PTY-002` und ist nicht gebaut. Ein Verweis, der
 * die Schnellerfassung verlaesst, vernichtet damit alles Getippte, und zwar in
 * dem Augenblick, in dem die Nutzerin dem Rat der Anwendung folgt (NFR-UX-005).
 *
 * Der Preis ist benannt und wird nicht verschwiegen: ein erzwungener neuer Tab
 * ist eine Aenderung ohne Anforderung (WCAG 3.2.5), solange sie nicht
 * angekuendigt ist. Sie ist deshalb SICHTBAR angekuendigt -- im Linktext, fuer
 * alle, und nicht nur fuer Hilfsmittel. Sobald ein Entwurfsschutz existiert,
 * gehoert dieser Verweis in dieselbe Seite. Owner: ST-PTY-002.
 */
export function candidateLinkLabel(displayName: string): string {
  return `${displayName} öffnen (${OPEN_CANDIDATE_HINT})`;
}

/**
 * WORAN der Treffer liegt -- das unterscheidende Merkmal aus `CM-CAP-005`.
 *
 * Ohne diese Angabe kann die Nutzerin einen starken Treffer nicht von einem
 * schwachen unterscheiden: gleiche E-Mail UND gleiche Nummer ist etwas anderes
 * als nur die Zentrale. Die Formulierung nennt den Kanal und nie den Wert --
 * den hat sie selbst getippt.
 */
export function matchReason(candidate: DuplicateCandidate): string {
  if (candidate.matchesEmailAddress && candidate.matchesPhoneNumber) {
    return 'gleiche E-Mail-Adresse und gleiche Telefonnummer';
  }
  if (candidate.matchesEmailAddress) {
    return 'gleiche E-Mail-Adresse';
  }
  if (candidate.matchesPhoneNumber) {
    return 'gleiche Telefonnummer';
  }
  // Nicht erreichbar: die API liefert einen Kandidaten nur mit mindestens einem
  // Treffer. Der Rueckfall behauptet trotzdem nichts, statt eine leere Klammer
  // zu zeigen -- dieselbe Haltung wie `labelFor` in parties.ts.
  return 'Übereinstimmung in einem Kontaktweg';
}

/**
 * Die Zusammenfassung fuer die Statusregion.
 *
 * IN WORTEN UND MIT DER ANZAHL, weil ein Screenreader die Liste sonst erst
 * durchlaufen muss, um zu wissen, wie gross sie ist (NFR-ACC-001).
 *
 * `hasMore` bekommt einen eigenen Satz, und er sagt die fachliche Bedeutung
 * statt der technischen: mehr als eine Handvoll Treffer auf denselben Wert
 * heisst fast immer, dass der Wert eine Sammeladresse ist -- und dann ist er
 * als Hinweis auf eine bestimmte Person wertlos. Eine Zahl steht dort
 * absichtlich nicht; die API liefert keine.
 */
export function duplicateSummary(count: number, hasMore: boolean): string {
  const treffer =
    count === 1
      ? 'Ein vorhandener Kontakt hat dieselben Angaben.'
      : `${count} vorhandene Kontakte haben dieselben Angaben.`;
  return hasMore
    ? `${treffer} Es sind noch mehr — die Angabe wird offenbar von mehreren geteilt.`
    : treffer;
}

/** Die Hauptaktion, nachdem eine Warnung steht. Eine Aktion, zwei Beschriftungen. */
export const CREATE_LABEL = 'Anlegen';
export const CREATE_ANYWAY_LABEL = 'Trotzdem neu anlegen';

export interface DuplicateCheckOptions {
  readonly baseUrl: string;
  readonly accessToken: string;
  readonly email?: string | undefined;
  readonly phone?: string | undefined;
  readonly send?: SendLike;
}

function factsOf(response: Response, payload: unknown): ResponseFacts {
  const body = (payload ?? {}) as Record<string, unknown>;
  return {
    status: response.status,
    answered: payload !== undefined,
    ...correlationOf(body),
  };
}

/**
 * Eine Kandidatenzeile, vollstaendig oder gar nicht.
 *
 * `undefined` heisst VERTRAGSBRUCH und nicht "diese Zeile lassen wir eben
 * weg". Der Unterschied ist die ganze Behebung des Befundes aus dem
 * Zweitreview an #208: eine Liste, aus der eine kaputte Zeile stillschweigend
 * fehlt, sieht vollstaendig aus und ist es nicht -- und diese Liste beantwortet
 * die Frage "gibt es diesen Kontakt schon". Eine fehlende Zeile ist dort keine
 * Luecke in der Anzeige, sondern eine Entwarnung, die niemand geprueft hat.
 *
 * Gelesen wird Feld fuer Feld und nicht per Spread -- dieselbe Richtung wie
 * `responseFieldPaths` auf der Serverseite, nur von der anderen Seite her: was
 * die API zusaetzlich schickte, erreicht den Bildschirm nicht.
 *
 * MINDESTENS EIN TREFFERKENNZEICHEN ist Pflicht. Die API liefert einen
 * Kandidaten nur mit mindestens einem Treffer; kaeme einer ohne, zeigte der
 * Bildschirm einen Namen ohne das unterscheidende Merkmal, das `CM-CAP-005`
 * ausdruecklich verlangt.
 */
function candidateOf(entry: unknown): DuplicateCandidate | undefined {
  if (typeof entry !== 'object' || entry === null || Array.isArray(entry)) {
    return undefined;
  }
  const row = entry as Record<string, unknown>;
  const text = (key: string): string | undefined =>
    typeof row[key] === 'string' && (row[key] as string).length > 0 ? (row[key] as string) : undefined;

  const partyId = text('partyId');
  const displayName = text('displayName');
  const kind = text('kind');
  const lifecycleStatus = text('lifecycleStatus');
  if (partyId === undefined || displayName === undefined || kind === undefined || lifecycleStatus === undefined) {
    return undefined;
  }
  if (typeof row['matchesEmailAddress'] !== 'boolean' || typeof row['matchesPhoneNumber'] !== 'boolean') {
    return undefined;
  }
  if (row['matchesEmailAddress'] === false && row['matchesPhoneNumber'] === false) {
    return undefined;
  }
  return {
    partyId,
    displayName,
    kind,
    lifecycleStatus,
    matchesEmailAddress: row['matchesEmailAddress'],
    matchesPhoneNumber: row['matchesPhoneNumber'],
  };
}

/**
 * Der vollstaendige Erfolgsvertrag der Antwort.
 *
 * `undefined` heisst: das war keine Auskunft von LIAISON, sondern etwas, das
 * sich dafuer ausgibt -- dieselbe Formulierung und derselbe Grund wie bei
 * `fetchParty` in parties.ts. Der praktische Fall ist nicht erfunden: der
 * statische Host hat am 31.08.2026 eine eigene Seite mit Status 200
 * ausgeliefert, und `fetchAccessSnapshot` behandelt genau das.
 *
 * Geprueft werden die TRAGENDEN Felder -- `candidates` und `hasMore` -- und
 * jede Zeile. `correlationId` gehoert bewusst NICHT dazu: sie ist eine
 * Diagnoseangabe, und ihr Fehlen macht die Auskunft nicht falsch. Dieselbe
 * Grenze zieht `fetchParty`, das auf `partyId` und `displayName` prueft und
 * nicht auf die Vorgangsnummer.
 */
function checkedResultOf(payload: unknown): DuplicateCheckResult | undefined {
  if (typeof payload !== 'object' || payload === null || Array.isArray(payload)) {
    return undefined;
  }
  const body = payload as Record<string, unknown>;
  if (!Array.isArray(body['candidates']) || typeof body['hasMore'] !== 'boolean') {
    return undefined;
  }
  const candidates: DuplicateCandidate[] = [];
  for (const entry of body['candidates']) {
    const candidate = candidateOf(entry);
    if (candidate === undefined) {
      return undefined;
    }
    candidates.push(candidate);
  }
  return { outcome: 'geprueft', candidates, hasMore: body['hasMore'] };
}

/**
 * Fragt die Kandidaten ab.
 *
 * EIN POST, OBWOHL NICHTS GESCHRIEBEN WIRD, und die Begruendung steht auf der
 * Serverseite: der getippte Wert ist ein Personendatum und hat in einer
 * Abfragezeichenkette nichts zu suchen -- dort landete er in Zugriffsprotokoll,
 * Browserhistorie und Referrer (NFR-PRV-001).
 *
 * LEERE FELDER WERDEN WEGGELASSEN, wie in `buildRequestBody`: ein leerer String
 * waere "ausdruecklich leer" und wuerde serverseitig als unbrauchbarer Wert
 * abgewiesen, obwohl die Nutzerin schlicht nichts eingetragen hat.
 */
export async function fetchDuplicateCandidates(options: DuplicateCheckOptions): Promise<DuplicateCheckResult> {
  const send = options.send ?? fetch;
  const email = options.email?.trim() ?? '';
  const phone = options.phone?.trim() ?? '';
  const body = {
    ...(email.length > 0 ? { email } : {}),
    ...(phone.length > 0 ? { phone } : {}),
  };

  const response = await send(`${options.baseUrl}/party-duplicate-candidates`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      authorization: `Bearer ${options.accessToken}`,
    },
    body: JSON.stringify(body),
  });
  const payload = await response.json().catch(() => undefined);

  if (!response.ok) {
    const record = (payload ?? {}) as Record<string, unknown>;
    return {
      outcome: 'ungeprueft',
      message: plainMessageFor(
        typeof record.reasonCode === 'string' ? record.reasonCode : undefined,
        factsOf(response, payload),
      ),
    };
  }

  // EINE 200 IST NOCH KEINE ANTWORT. Erst der vollstaendige Vertrag macht sie
  // zu einer -- alles andere ist `ungeprueft` und ausdruecklich keine leere
  // Kandidatenliste. Befund des Zweitreviews an #208.
  const checked = checkedResultOf(payload);
  if (checked === undefined) {
    return {
      outcome: 'ungeprueft',
      // `answered: false` und nicht der Status: die Antwort kam an, sie war nur
      // keine. Genau diese Unterscheidung traegt der Rueckfalltext in
      // access.ts, und sie ist dieselbe wie bei einer 200 mit HTML.
      message: plainMessageFor(undefined, { ...factsOf(response, payload), answered: false }),
    };
  }
  return checked;
}

/**
 * Lohnt die Pruefung ueberhaupt?
 *
 * Sie kann NUR auf E-Mail und Rufnummer treffen -- Namensaehnlichkeit ist ein
 * ausdrueckliches Nichtziel dieses Schnitts (`CM-SEA-002` verlangt ein
 * Referenzkorpus, und es gibt keins). Ohne einen der beiden Werte hat die
 * Pruefung nichts zu vergleichen; sie zu schicken erzeugte einen 422 und
 * verzoegerte die Anlage um eine Rundreise, ohne irgendetwas zu leisten.
 */
export function isCheckable(form: { readonly email: string; readonly phone: string }): boolean {
  return form.email.trim().length > 0 || form.phone.trim().length > 0;
}
